<script setup>
import { computed } from 'vue';
import { useMessageContext } from '../provider.js';
import { useAccountTimezone } from 'dashboard/composables/useAccountTimezone';
import { useMapGetter } from 'dashboard/composables/store';
import { messageStamp, messageTimestamp } from 'shared/helpers/timeHelper';

const { content, createdAt, sender, contentAttributes } = useMessageContext();
const { accountTimezone } = useAccountTimezone();
const currentUser = useMapGetter('getCurrentUser');

const eventType = computed(() => {
  const attrs = contentAttributes.value || {};
  return String(attrs.systemEventType || attrs.system_event_type || '').trim();
});

const payload = computed(() => {
  const attrs = contentAttributes.value || {};
  return attrs.systemEventPayload || attrs.system_event_payload || {};
});

const rawContent = computed(() => String(content.value || '').trim());
const senderName = computed(() => String(sender.value?.name || '').trim());

const parseAgentNameFromClosedText = text => {
  const match = String(text || '')
    .trim()
    .match(/^(.+?)\s+has\s+closed\s+the\s+chat\.?$/i);
  return match?.[1] ? String(match[1]).trim() : '';
};

const parseTransferContent = text => {
  const raw = String(text || '').trim();
  const match = raw.match(/^Transferred\s*[–-]\s*by\s+(.+?)\s+to\s+(.+)$/i);
  if (!match) return null;
  const by = String(match[1] || '').trim();
  const targetRaw = String(match[2] || '').trim();
  const inParen = targetRaw.match(/\(([^)]+)\)\s*$/);
  const to = inParen?.[1] ? String(inParen[1]).trim() : targetRaw;
  if (!by || !to) return null;
  return { by, to };
};

const EMOJI_VALUE_MAP = { 1: '😞', 2: '😑', 3: '😐', 4: '😀', 5: '😍' };

const normalizeRating = (eventPayload, text) => {
  const fromPayload = String(eventPayload?.rating || '')
    .trim()
    .toLowerCase();
  if (fromPayload === 'good' || fromPayload === 'bad') return fromPayload;
  const raw = String(text || '').toLowerCase();
  if (raw.includes(' as good')) return 'good';
  if (raw.includes(' as bad')) return 'bad';
  return '';
};

const payloadValue = (eventPayload, snakeKey, camelKey) => {
  if (!eventPayload || typeof eventPayload !== 'object') return undefined;
  if (Object.prototype.hasOwnProperty.call(eventPayload, camelKey)) {
    return eventPayload[camelKey];
  }
  return eventPayload[snakeKey];
};

const inferInactivityMinutes = (eventPayload, text) => {
  const payloadMinutes =
    Number(
      payloadValue(eventPayload, 'inactive_minutes', 'inactiveMinutes') ||
        payloadValue(eventPayload, 'inactivity_minutes', 'inactivityMinutes')
    ) || 0;
  if (payloadMinutes > 0) return payloadMinutes;

  const raw = String(text || '');
  const minuteMatch = raw.match(/(\d+)\s*minutes?\s+of\s+inactivity/i);
  if (minuteMatch?.[1]) {
    const parsed = Number(minuteMatch[1]);
    if (parsed > 0) return parsed;
  }
  return 15;
};

const activityData = computed(() => {
  const raw = rawContent.value;
  const type = eventType.value;
  const eventPayload = payload.value || {};

  if (
    type === 'auto_closed_inactive' ||
    /^Chat ended automatically due to your inactivity/i.test(raw)
  ) {
    // Captain AI auto-close tags payload.closed_by='ai' so we can show a
    // distinct "closed by AI" line (parallels "closed by agent {name}").
    // The STANDARD inactivity timeout has no closed_by flag, so it falls
    // through to the original "N min inactivity" text unchanged.
    const closedBy = String(
      payloadValue(eventPayload, 'closed_by', 'closedBy') || ''
    )
      .trim()
      .toLowerCase();
    if (closedBy === 'ai') {
      return { kind: 'default', text: 'Archived - closed by AI', quote: '' };
    }
    const minutes = inferInactivityMinutes(eventPayload, raw);
    return {
      kind: 'default',
      text: `Archived - ${minutes} min inactivity`,
      quote: '',
    };
  }

  if (
    type === 'rating_submitted' ||
    /^You rated our customer service as /i.test(raw)
  ) {
    const emojiVal = Number(
      eventPayload?.emoji_value ?? eventPayload?.emojiValue ?? 0
    );
    if (emojiVal >= 1 && emojiVal <= 5) {
      const emoji = EMOJI_VALUE_MAP[emojiVal];
      return { kind: 'rated_emoji', text: `Chat rated ${emoji}`, quote: '' };
    }
    const rating = normalizeRating(eventPayload, raw);
    if (rating === 'good') {
      return { kind: 'rated_good', text: 'Chat rated good', quote: '' };
    }
    if (rating === 'bad') {
      return { kind: 'rated_bad', text: 'Chat rated bad', quote: '' };
    }
    return { kind: 'default', text: 'Chat rated', quote: '' };
  }

  if (
    type === 'rating_comment_submitted' ||
    /^You left the following comment: /i.test(raw)
  ) {
    const comment =
      String(eventPayload?.comment || '').trim() ||
      raw.replace(/^You left the following comment:\s*/i, '').trim();
    return {
      kind: 'comment',
      text: 'Customer left the comment',
      quote: comment,
    };
  }

  if (type === 'contact_closed' || /^You have closed the chat\./i.test(raw)) {
    return {
      kind: 'default',
      text: 'Archived - customer left the chat',
      quote: '',
    };
  }

  if (type === 'agent_closed' || /has closed the chat/i.test(raw)) {
    const fromPayload = String(
      payloadValue(eventPayload, 'agent_name', 'agentName') || ''
    ).trim();
    const fromRaw = parseAgentNameFromClosedText(raw);
    const fromSender = senderName.value;
    const agentName = fromPayload || fromRaw || fromSender || 'Agent';
    return {
      kind: 'default',
      text: `Archived - closed by agent ${agentName}`,
      quote: '',
    };
  }

  if (type === 'agent_took_over') {
    const by = String(
      payloadValue(eventPayload, 'previous_agent_name', 'previousAgentName') ||
        ''
    ).trim();
    const to = String(
      payloadValue(eventPayload, 'agent_name', 'agentName') || ''
    ).trim();
    if (by && to) {
      return {
        kind: 'default',
        text: `Transferred - by ${by} to ${to}`,
        quote: '',
      };
    }
  }

  const parsedTransfer = parseTransferContent(raw);
  if (parsedTransfer) {
    return {
      kind: 'default',
      text: `Transferred - by ${parsedTransfer.by} to ${parsedTransfer.to}`,
      quote: '',
    };
  }

  if (/^Idle\s*-\s*\d+\s*min\s*inactivity/i.test(raw)) {
    return { kind: 'default', text: raw, quote: '' };
  }

  if (
    /^Conversation was (marked )?resolved by /i.test(raw) ||
    /^Conversation was marked resolved by system due to /i.test(raw)
  ) {
    return { kind: 'default', text: '', quote: '' };
  }

  return { kind: 'default', text: raw, quote: '' };
});

const activityText = computed(() => activityData.value.text || '');
const activityQuote = computed(() => activityData.value.quote || '');
const isCommentActivity = computed(() => activityData.value.kind === 'comment');
const isRatedGoodActivity = computed(
  () => activityData.value.kind === 'rated_good'
);
const isRatedBadActivity = computed(
  () => activityData.value.kind === 'rated_bad'
);
const isRatedEmojiActivity = computed(
  () => activityData.value.kind === 'rated_emoji'
);

const readableTime = computed(() =>
  messageTimestamp(createdAt.value, 'LLL d, h:mm a', accountTimezone.value)
);

const shortTime = computed(() =>
  messageStamp(createdAt.value, 'h:mm a', accountTimezone.value)
);
</script>

<template>
  <div
    v-if="activityText"
    class="wlab-system-activity"
    data-testid="system-message"
  >
    <div
      v-tooltip.top="readableTime"
      class="wlab-system-activity__header"
      data-testid="system-message-header"
    >
      <div
        class="wlab-system-activity__content"
        data-testid="system-message-title"
      >
        <div class="wlab-system-activity__line">
          <span
            v-if="isRatedGoodActivity"
            class="wlab-system-activity__icon wlab-system-activity__icon--good"
            aria-hidden="true"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              width="16"
              height="16"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="m13.655 3.39-3.7 4.858c-.331.324-.519.772-.519 1.237v8.761c0 .965.805 1.754 1.788 1.754h5.006a1.78 1.78 0 0 0 1.635-1.061l2.914-6.674c.76-1.737-.536-3.666-2.458-3.666h-3.263l.85-4.017a1.3 1.3 0 0 0-.367-1.202 1.35 1.35 0 0 0-1.886.01M4.788 20c.983 0 1.788-.79 1.788-1.754V11.23c0-.965-.805-1.754-1.788-1.754-.984 0-1.788.789-1.788 1.754v7.016C3 19.211 3.804 20 4.788 20"
              />
            </svg>
          </span>
          <span
            v-if="isRatedBadActivity"
            class="wlab-system-activity__icon wlab-system-activity__icon--bad"
            aria-hidden="true"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              width="16"
              height="16"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="m13.655 20.61-3.7-4.858a1.74 1.74 0 0 1-.519-1.237V5.755c0-.965.805-1.754 1.788-1.754h5.006a1.78 1.78 0 0 1 1.635 1.061l2.914 6.674c.76 1.737-.536 3.666-2.458 3.666h-3.263l.85 4.017c.09.439-.045.886-.367 1.202a1.35 1.35 0 0 1-1.886-.01M4.788 4c.983 0 1.788.79 1.788 1.754v7.016c0 .965-.805 1.754-1.788 1.754-.984 0-1.788-.789-1.788-1.754V5.754C3 4.79 3.804 4 4.788 4"
              />
            </svg>
          </span>
          <span
            v-if="isRatedEmojiActivity"
            class="wlab-system-activity__icon wlab-system-activity__icon--emoji"
            aria-hidden="true"
          />
          <p class="wlab-system-activity__title privacy-masker">
            {{ activityText }}
          </p>
        </div>
        <q
          v-if="isCommentActivity && activityQuote"
          class="wlab-system-activity__quote privacy-masker"
        >
          {{ activityQuote }}
        </q>
      </div>
      <div data-testid="tooltip-trigger">
        <div
          class="wlab-system-activity__timestamp"
          data-testid="system-message-timestamp-value"
        >
          • {{ shortTime }}
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.wlab-system-activity {
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  margin: 20px 40px 20px 0;
}

.wlab-system-activity__header {
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
}

.wlab-system-activity__content {
  display: flex;
  justify-content: center;
  align-items: flex-end;
  flex-direction: column;
}

.wlab-system-activity__line {
  display: flex;
  align-items: center;
}

.wlab-system-activity__icon {
  margin-right: 7px;
  display: flex;
  align-items: center;
}

.wlab-system-activity__icon svg {
  pointer-events: none;
}

.wlab-system-activity__icon--good {
  color: #00893c;
}

.wlab-system-activity__icon--bad {
  color: #ca0005;
}

.wlab-system-activity__title {
  line-height: 16px;
  letter-spacing: 0;
  font-size: 12px;
  font-weight: 400;
  color: #62626d;
  margin: 0;
}

.wlab-system-activity__quote {
  color: #62626d;
  font-style: italic;
  max-width: 100%;
  overflow-wrap: break-word;
  word-break: break-word;
  text-align: right;
  line-height: 16px;
  letter-spacing: 0;
  font-size: 12px;
  font-weight: 400;
}

.wlab-system-activity__timestamp {
  color: #62626d;
  margin-left: 4px;
  text-align: end;
  line-height: 16px;
  letter-spacing: 0;
  font-size: 12px;
  font-weight: 400;
}
</style>
